/**
 * Keeps the "incl. btw" line in sync with the tax-exclusive price beside it.
 *
 * Liquid renders both amounts server-side, which already covers page load and
 * variant changes — the theme refetches the whole price block on a variant
 * switch. Globo Product Options is the gap: when an add-on is selected it
 * rewrites the tax-exclusive amount in place (see the `unitPrice` selectors it
 * receives from its app embed), and Liquid never hears about it.
 *
 * So we mirror the amount that is actually on screen: read it, apply the VAT
 * rate, rewrite this element. Mirroring the rendered price — rather than
 * recomputing from variant plus add-on data — keeps one source of truth, cannot
 * double-count, and works for any app that touches the price without depending
 * on that app's internals.
 */
if (!customElements.get('price-incl-tax')) {
  customElements.define(
    'price-incl-tax',
    class PriceInclTax extends HTMLElement {
      // Where the theme puts the tax-exclusive amount, and therefore where an
      // add-on app overwrites it. Only one branch can match: a price is either
      // on sale or it is not.
      static PRICE_SELECTOR = [
        '.f-price:not(.f-price--on-sale) .f-price__regular .f-price-item--regular',
        '.f-price.f-price--on-sale .f-price__sale .f-price-item--sale',
      ].join(',');

      static PRICE_PLACEHOLDER = '__PRICE__';

      connectedCallback() {
        this.wrapper = this.closest('.product__price-wrapper');
        if (!this.wrapper) return;

        this.observer = new MutationObserver((mutations) => {
          // Our own write lands inside the subtree we watch, so skip it or we
          // would retrigger ourselves indefinitely.
          if (mutations.every((mutation) => this.contains(mutation.target))) return;
          this.render();
        });
        this.observer.observe(this.wrapper, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.render();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      render() {
        const label = this.buildLabel();

        // Rather no figure at all than a stale one next to a price that moved.
        if (label === null) {
          this.hidden = true;
          return;
        }

        if (this.textContent.trim() !== label) this.textContent = label;
        this.hidden = false;
      }

      /** @returns {string|null} The full label, or null if it cannot be trusted. */
      buildLabel() {
        const priceExclTax = this.readPriceExclTax();
        if (priceExclTax === null || priceExclTax <= 0) return null;

        const taxRate = parseFloat(this.dataset.taxRate);
        if (!Number.isFinite(taxRate)) return null;

        const money = this.formatMoney(Math.round(priceExclTax * (1 + taxRate / 100)));
        if (money === null) return null;

        const template = this.dataset.labelTemplate || PriceInclTax.PRICE_PLACEHOLDER;
        return template.replace(PriceInclTax.PRICE_PLACEHOLDER, money);
      }

      /** @returns {number|null} The visible tax-exclusive amount, in cents. */
      readPriceExclTax() {
        const node = this.wrapper.querySelector(PriceInclTax.PRICE_SELECTOR);
        return node ? PriceInclTax.parseMoney(node.textContent) : null;
      }

      /** @returns {string|null} Formatted money, or null if the theme helper is missing. */
      formatMoney(cents) {
        const format = window.FoxTheme?.settings?.moneyFormat;
        const formatMoney = window.FoxTheme?.Currency?.formatMoney;
        if (!format || !formatMoney) return null;

        return PriceInclTax.stripTrailingZeros(formatMoney(cents, format));
      }

      /**
       * Parses a formatted amount back into cents.
       *
       * Two formats reach us. The theme drops a trailing ",00" (so "€379"),
       * while an add-on app writes its own format and keeps the decimals
       * ("€413,50"). Which of "." and "," separates the decimals also varies per
       * money format, so decide from the separator's position rather than
       * assuming: one or two trailing digits mean decimals, anything else means
       * grouping.
       *
       * @returns {number|null} Amount in cents, or null if unparseable.
       */
      static parseMoney(text) {
        const cleaned = String(text ?? '').replace(/[^\d.,]/g, '');
        if (!/\d/.test(cleaned)) return null;

        const separator = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
        const trailing = separator === -1 ? '' : cleaned.slice(separator + 1);
        const hasDecimals = /^\d{1,2}$/.test(trailing);

        const units = (hasDecimals ? cleaned.slice(0, separator) : cleaned).replace(/[.,]/g, '');
        if (!/^\d+$/.test(units)) return null;

        const decimals = hasDecimals ? parseInt(trailing.padEnd(2, '0'), 10) : 0;
        return parseInt(units, 10) * 100 + decimals;
      }

      /** Mirrors the price-strip-zeros snippet, so both prices read the same way. */
      static stripTrailingZeros(money) {
        return String(money)
          .trim()
          .replace(/[.,]00$/, '')
          .replace(/[.,]00 /, ' ');
      }
    }
  );
}
